import SentryLogger from './sentry';
import { errorMessages } from './errorMessages';
import { type ErrorMessages, type SeverityLevel } from './types';
import ENV from '../ENV';

/**
 * Format error messages and log to console or Sentry,
 * based on context of the environment and severity level.
 */
export default class Logger {
  private static formatMessage = (
    functionName: string,
    message: string | ((x: string) => string),
    additionalDetails?: string
  ) => {
    return `(${functionName}) ${
      typeof message === 'function' ? message(additionalDetails ?? '') : message
    }`;
  };

  private static logToSentry(
    level: SeverityLevel,
    message: string,
    eventData?: Record<string, unknown>
  ) {
    // Most logs we will just captureEvent, this allows more information to be captured.
    if (level !== 'info') {
      SentryLogger.captureEvent(message, level, eventData);
    } else {
      SentryLogger.captureMsg(message);
    }
  }

  private static logToConsole(
    level: SeverityLevel,
    message: string,
    eventData?: Record<string, unknown>
  ) {
    if (level === 'error' || level === 'warning') {
      console.warn(message, eventData);
    } else if (level === 'fatal') {
      console.error(message, eventData);
    } else {
      console.log(message, eventData);
    }
  }

  /** General-use log function */
  public static captureEvent<K extends keyof ErrorMessages>(
    level: SeverityLevel,
    functionName: K,
    messageKey: keyof ErrorMessages[K],
    additionalInfo: {
      additionalMsg?: string;
      eventData?: Record<string, unknown>;
    }
  ) {
    // Fetch and format message
    const msgContent = errorMessages[functionName][messageKey];
    const msg = Logger.formatMessage(
      functionName,
      msgContent as string | ((x: string) => string),
      additionalInfo.additionalMsg
    );

    if (ENV.SENTRY_ENV !== 'local') {
      Logger.logToSentry(level, msg, additionalInfo.eventData);
    } else {
      Logger.logToConsole(level, msg, additionalInfo.eventData);
    }
  }
}
